import type {
	DefaultConsumerInputs,
	QuestionnaireStep,
	QuestionnaireStepValidationParameters,
} from '#modules/Questionnaire/types/questionnaire.types'
import type { FormikErrors, FormikProps } from 'formik'
import type { ChangeEventHandler } from 'react'

import { FlowPage, FormItem } from '@monthio/design-system'
import {
	FormInput,
	getNationalIdMaxLength,
	Region,
} from '@monthio/react-components'
import { useField } from 'formik'
import i18n from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { Nationality } from '../../../../api/budgetApiClient'
import type { Options } from './personalInformation.types'

import { budgetApiClient } from '../../../../api/budgetApiClient'
import { ScrollToViewContainer } from '../../../../modulesTemp/shared/ScrollToViewContainer'
import { NationalityInput } from './NationalityInput'
import { validateEmail } from './validators/emailValidator'
import { createPersonalInformationValidator } from './validators/personalInformationValidator'
import validateUploadedFiles from './validators/uploadedFilesValidator'

const parseNumber = (value: string) => value.replaceAll(/([^\d-])/g, '')
const parseNumberAndLetter = (value: string) =>
	value.replaceAll(/([^\dA-Z])/g, '')

const StyledFormInput = styled(FormInput)`
	width: 100%;
`

const PersonalInformationStepComponent = ({
	formProps,
	isValidating,
	regionId,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
	isValidating?: boolean
	regionId: Region
}): JSX.Element =>
	PersonalInformationBaseStep({ formProps, isValidating, regionId }, false)

const PersonalInformationExtendedStepComponent = ({
	formProps,
	isValidating,
	regionId,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
	isValidating?: boolean
	regionId: Region
}): JSX.Element => {
	const [listOfNationalities, setListOfNationalities] =
		useState<Options | null>(null)

	useEffect(() => {
		budgetApiClient
			.getNationalities()
			.then((value: Nationality[]) => {
				const nationalities: Options = value.map((x) => {
					return {
						value: x.code,
						displayValue: i18n.t(
							`consumerInputs-personalInformation-nationality-${x.code}`,
						),
					}
				})
				setListOfNationalities(
					nationalities
						? [
								...nationalities,
								{
									value: 'other',
									displayValue: i18n.t(
										'consumerInputs-personalInformation-nationality-other',
									),
								},
								{ value: '', displayValue: '' },
							]
						: [],
				)
			})
			.catch(() => {})
	}, [])
	return PersonalInformationBaseStep(
		{ formProps, isValidating, regionId },
		true,
		listOfNationalities,
	)
}

const PersonalInformationCprOnlyStepComponent = ({
	formProps,
	regionId,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
	regionId: Region
}): JSX.Element => {
	const { t } = useTranslation()
	useEffect(() => {
		formProps.setSubmitting(false)
	}, [])

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-personalInformation-title')}
				description={t('consumerInputs-personalInformation-subtitle')}
			/>
			<ScrollToViewContainer>
				<NationalIdentificationInput regionId={regionId} />
			</ScrollToViewContainer>
		</FlowPage.Content>
	)
}

const NationalIdentificationNumberInputField = ({
	regionId,
}: {
	regionId: Region
}) => {
	const [_, { error, touched }, { setValue }] = useField(
		'personalInformation.cprNumber',
	)

	const handleCprInput = useCallback(
		(event) => {
			let cpr
			switch (regionId) {
				case Region.Finland:
					cpr = event.target.value
					break
				case Region.Spain:
					cpr = parseNumberAndLetter(event.target.value)
					break
				default:
					cpr = parseNumber(event.target.value)
			}
			setValue(cpr)
		},
		[setValue, regionId],
	)

	return (
		<StyledFormInput
			id="personalInformation.cprNumber"
			autoComplete="off"
			maxLength={getNationalIdMaxLength(regionId)}
			label={undefined}
			FormHelperTextProps={{
				error: touched && !!error,
			}}
			onChange={handleCprInput}
		/>
	)
}

const useNationalIdInputHelpText = (regionId: Region): string => {
	const { t } = useTranslation()

	switch (regionId) {
		case Region.Sweden:
			return t(
				'se-consumerInputs-personalInformation-identityNumber-helperText',
			)
		case Region.Norway:
			return t(
				'no-consumerInputs-personalInformation-identityNumber-helperText',
			)
		case Region.Finland:
			return t(
				'fi-consumerInputs-personalInformation-identityNumber-helperText',
			)
		case Region.Spain:
			return t(
				'es-consumerInputs-personalInformation-identityNumber-helperText',
			)
		default:
			return ''
	}
}

const PersonalInformationBaseStep = (
	{
		formProps,
		regionId,
	}: {
		formProps: FormikProps<DefaultConsumerInputs>
		isValidating?: boolean
		regionId: Region
	},
	isExtended = false,
	listOfNationalities: Options | null = null,
) => {
	const { t } = useTranslation()
	useEffect(() => {
		formProps.setSubmitting(false)
	}, [])

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-personalInformation-title')}
				description={t('consumerInputs-personalInformation-subtitle')}
			/>
			<ScrollToViewContainer>
				<NationalIdentificationInput regionId={regionId} />
			</ScrollToViewContainer>
			<ScrollToViewContainer>
				<PhoneNumberInput />
			</ScrollToViewContainer>
			<ScrollToViewContainer>
				<EmailInput />
			</ScrollToViewContainer>
			{isExtended && listOfNationalities && (
				<NationalityInput
					formProps={formProps}
					listOfNationalities={listOfNationalities}
				/>
			)}
		</FlowPage.Content>
	)
}

const NationalIdentificationInput = ({ regionId }: { regionId: Region }) => {
	const { t } = useTranslation()
	const [_field, meta] = useField('personalInformation.cprNumber')
	const nationalIdentityInputHelpText = useNationalIdInputHelpText(regionId)

	return (
		<FormItem
			label={t('cpr-number')}
			isRequired
			requiredText={t('required')}
			hintText={nationalIdentityInputHelpText}
			errorText={meta.touched ? meta.error : undefined}
		>
			<NationalIdentificationNumberInputField regionId={regionId} />
		</FormItem>
	)
}

const PhoneNumberInput = () => {
	const { t } = useTranslation()
	const [field, meta, helpers] = useField<string>(
		'personalInformation.phoneNumber',
	)
	const handleNumberInput: ChangeEventHandler<
		HTMLInputElement | HTMLTextAreaElement
	> = useCallback(
		(event) => {
			helpers.setValue(parseNumber(event.target.value), true)
		},
		[helpers],
	)
	return (
		<FormItem
			label={t('phone-number')}
			isRequired
			requiredText={t('required')}
			errorText={meta.touched ? meta.error : undefined}
		>
			<StyledFormInput
				id="personalInformation.phoneNumber"
				type="tel"
				maxLength={20}
				pattern="\d*"
				{...field}
				value={field.value || ''}
				onChange={handleNumberInput}
			/>
		</FormItem>
	)
}

const EmailInput = () => {
	const { t } = useTranslation()
	const [field, meta] = useField('personalInformation.email')

	return (
		<FormItem
			label={t('email')}
			isRequired
			requiredText={t('required')}
			errorText={meta.touched ? meta.error : undefined}
		>
			<StyledFormInput
				id="personalInformation.email"
				type="email"
				maxLength={100}
				{...field}
			/>
		</FormItem>
	)
}

const PersonalInformationStepValidation = async ({
	values,
	errors,
	regionId,
	validateCprAgainstServer,
}: QuestionnaireStepValidationParameters): Promise<
	FormikErrors<DefaultConsumerInputs>
> => {
	const validator = createPersonalInformationValidator(regionId)
	const cpr_validation = await validator.validateCpr(
		values.personalInformation?.cprNumber,
		validateCprAgainstServer,
	)
	if (cpr_validation.success && values.personalInformation) {
		values.personalInformation.cprNumber = cpr_validation.output
	} else {
		errors.personalInformation = {
			...errors.personalInformation,
			cprNumber: cpr_validation.output,
		}
	}

	const phoneNumber_validation = validator.validatePhoneNumber(
		values.personalInformation?.phoneNumber,
	)
	if (!phoneNumber_validation.success) {
		errors.personalInformation = {
			...errors.personalInformation,
			phoneNumber: phoneNumber_validation.output,
		}
	}

	const email_validation = validateEmail(values.personalInformation?.email)
	if (!email_validation.success) {
		errors.personalInformation = {
			...errors.personalInformation,
			email: email_validation.output,
		}
	}

	const uploadedFiles_validation = validateUploadedFiles(
		values.idDocumentNationalityExemptions ?? null,
		values.personalInformation?.nationalities,
		values.uploadedFiles,
	)
	if (!uploadedFiles_validation.success) {
		errors = {
			...errors,
			uploadedFiles: uploadedFiles_validation.output,
		}
	}

	if (
		values.personalInformation &&
		!values.personalInformation.nationalities.every(Boolean)
	) {
		errors.personalInformation = {
			...errors.personalInformation,
			nationalities: i18n.t('validation-select-nationalities'),
		}
	}
	return errors
}

const PersonalInformationCprOnlyStepValidation = async ({
	values,
	errors,
	regionId,
	validateCprAgainstServer,
}: QuestionnaireStepValidationParameters): Promise<
	FormikErrors<DefaultConsumerInputs>
> => {
	const validator = createPersonalInformationValidator(regionId)
	const cpr_validation = await validator.validateCpr(
		values.personalInformation?.cprNumber,
		validateCprAgainstServer,
	)
	if (cpr_validation.success && values.personalInformation) {
		values.personalInformation.cprNumber = cpr_validation.output
	} else {
		errors.personalInformation = {
			...errors.personalInformation,
			cprNumber: cpr_validation.output,
		}
	}
	return errors
}

export const PersonalInformationStep: QuestionnaireStep = {
	validation: PersonalInformationStepValidation,
	component: PersonalInformationStepComponent,
}

export const PersonalInformationCprOnlyStep: QuestionnaireStep = {
	validation: PersonalInformationCprOnlyStepValidation,
	component: PersonalInformationCprOnlyStepComponent,
}

export const PersonalInformationExtendedStep: QuestionnaireStep = {
	validation: PersonalInformationStepValidation,
	component: PersonalInformationExtendedStepComponent,
}
